/*
 * Kurumsal fatura: sepette tek satır onay kutusu, işaretlenince pencere açılır.
 *
 * Öznitelik anahtarları Entegra'nın "Müşteri Bilgisi" formuyla birebir aynı:
 * TC No, Firma, Vergi Dairesi, Vergi No. Buradaki adları değiştirirsen Entegra
 * tarafındaki eşlemeyi de güncelle (entegra-fatura/…/bicim.ts).
 *
 * Pencere temanın `basic-modal` bileşeni. `requiresBodyAppended` olduğu için
 * açılışta <body> altına taşınıyor; bu yüzden alanlar `this` içinde DEĞİL,
 * pencere id'si üzerinden global aranıyor. `this.querySelector` kullanırsan
 * pencere bir kez açıldıktan sonra alanları bulamazsın.
 *
 * ## Firma türü (30 Ekim 2026)
 *
 * Zorunlu seçim, öznitelik `Firma Türü`: Şahıs / Limited / Anonim / Diğer.
 * Şahıs firmasının faturası sahibinin TC'siyle, tüzel kişininki VKN ile kesilir
 * (sahibinin kuralı); seçime göre yalnız biri görünür ve zorunludur, diğeri boş
 * gönderilir. Limited ve Anonim'de unvan eki ("Ltd. Şti." / "A.Ş.") Entegra'ya
 * giderken eklenir; burada yalnız kutunun sağında soluk gösteriliyor. Müşteri
 * eki zaten yazdıysa eklenmez — TUZEL ifadesi bicim.ts'dekiyle aynı olmalı.
 *
 * Alanlar ödeme formuna (`form="cart"`) bağlı değil; kayıt yalnız
 * /cart/update.js ile. Pencere Kaydet'e basılmadan kapanırsa: bilgi tamamsa
 * kaydedilir, eksikse kutu geri alınır (işaretli ama eksik kutu, müşteriye
 * bilgiyi verdiğini düşündürür).
 *
 * ## Doğrulama neden asimetrik
 *
 * TC no için resmî sağlama algoritması var. T-Soft yedeğindeki 1.273 gerçek
 * kimlik numarasında ölçüldü: %99'u geçiyor — yazım hatasını yakalıyor.
 *
 * Vergi no için SADECE hane sayısına bakılıyor. VKN sağlama algoritması denendi,
 * yedekteki 54 gerçek vergi numarasının yalnızca 23'ünü geçirdi ve uydurma bir
 * numarayı kabul etti. Yanlış çalışan doğrulayıcı, doğrulayıcı olmamasından kötü.
 */
function tcknGecerli(n) {
  if (!/^[0-9]{11}$/.test(n)) return false;
  if (n[0] === '0') return false;
  const d = n.split('').map(Number);
  const tek = d[0] + d[2] + d[4] + d[6] + d[8];
  const cift = d[1] + d[3] + d[5] + d[7];
  // Fark eksi çıkabiliyor; JS'te % eksi kalan veriyor, geçerli numara reddediliyordu.
  if ((((tek * 7 - cift) % 10) + 10) % 10 !== d[9]) return false;
  let t = 0;
  for (let i = 0; i < 10; i++) t += d[i];
  return t % 10 === d[10];
}

const NM_UNVAN_EKI = { Limited: 'Ltd. Şti.', Anonim: 'A.Ş.' };
// Harf olmayan bir şeyle ayrılmış ek: "KARDAŞ"taki AŞ'yi, "LİMAN"daki LİM'i yakalamasın.
const NM_TUZEL =
  /(^|[^A-ZÇĞİIÖŞÜ])(LTD|LİMİTED|LIMITED|LİM|LIM|ANONİM|ANONIM|A\.?\s?Ş|KOOPERATİF[A-ZÇĞİIÖŞÜ]*|KOOPERATIF[A-ZÇĞİIÖŞÜ]*)(?=$|[^A-ZÇĞİIÖŞÜ])/;

function unvanEki(tur, firma) {
  const ek = NM_UNVAN_EKI[tur];
  if (!ek) return '';
  return NM_TUZEL.test((firma || '').toLocaleUpperCase('tr-TR')) ? '' : ek;
}

class NmTaxInvoice extends HTMLElement {
  connectedCallback() {
    this.modalId = this.dataset.modalId;
    this.toggle = this.querySelector('[data-nm-tax-toggle]');
    this.ozet = this.querySelector('[data-nm-summary]');
    this.ozetMetin = this.querySelector('[data-nm-summary-text]');
    // Zorunlu alan uyarıları ilk Kaydet denemesinden sonra; yazarken bağırmasın.
    this.denendi = false;

    if (this.toggle) this.toggle.addEventListener('change', this.acKapa.bind(this));

    const m = this.modal();
    if (m) {
      m.querySelectorAll('input[type="text"]').forEach((a) => {
        a.addEventListener('input', () => {
          if (a.hasAttribute('data-nm-tax-tckn') || a.hasAttribute('data-nm-tax-no')) {
            a.value = a.value.replace(/[^0-9]/g, '');
          }
          this.kaydedildi = false;
          this.uyarilar();
          this.ekiGoster();
        });
      });
      m.querySelectorAll('[data-nm-tur]').forEach((r) => {
        r.addEventListener('change', () => {
          this.kaydedildi = false;
          this.turuUygula();
          this.uyarilar();
          // İlk seçimde alanlar yeni açılıyor; imleç unvana geçsin.
          const f = this.alan('[data-nm-tax-company]');
          if (f && !f.value) f.focus();
        });
      });
      const kaydet = m.querySelector('[data-nm-save]');
      if (kaydet) kaydet.addEventListener('click', this.kaydet.bind(this));
      m.addEventListener('modal:handleAfterHide', this.kapandi.bind(this));
    }

    this.turuUygula();
    // Alanlar sepet özniteliklerinden dolu geliyor: tamamsa kaydedilmiş demektir.
    this.kaydedildi = !!(this.toggle && this.toggle.checked) && this.tamamMi();
    this.uyarilar();
    this.ozetiTazele();
    this.yerlestir();
  }

  modal() {
    return this.modalId ? document.getElementById(this.modalId) : null;
  }

  alan(sec) {
    const m = this.modal();
    return m ? m.querySelector(sec) : null;
  }

  deger(sec) {
    return ((this.alan(sec) || {}).value || '').trim();
  }

  tur() {
    const r = this.alan('[data-nm-tur]:checked');
    return r ? r.value : '';
  }

  /*
   * Il/ilce uygulamasi kendini odeme butonunun kapsayicisina calisma aninda
   * enjekte ediyor; bu blok Liquid'de kendi yerinde. Farkli ebeveynler oldugu
   * icin sira Liquid'den ayarlanamiyor. Adres ZORUNLU, fatura ISTEGE BAGLI —
   * zorunlu olan ustte kalmali. Uygulama yoksa blok yerinde kaliyor.
   */
  yerlestir() {
    const kart = this.closest('.cart__addon');
    if (!kart) return;
    const tasi = () => {
      const il = document.querySelector('.il-ilce-app');
      if (!il || !il.parentElement) return false;
      if (il.nextElementSibling === kart) return true;
      il.parentElement.insertBefore(kart, il.nextSibling);
      return true;
    };
    if (tasi()) return;
    const g = new MutationObserver(() => {
      if (tasi()) g.disconnect();
    });
    g.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => g.disconnect(), 8000);
  }

  /* Seçime göre alanları açar, TC ile Vergi No'dan yalnız birini gösterir. */
  turuUygula() {
    const m = this.modal();
    if (!m) return;
    const tur = this.tur();
    const alanlar = this.alan('[data-nm-fields]');
    if (alanlar) alanlar.hidden = !tur;
    m.querySelectorAll('[data-nm-only]').forEach((k) => {
      k.hidden = (k.dataset.nmOnly === 'sahis') !== (tur === 'Şahıs');
    });
    const secili = this.alan('[data-nm-tur]:checked');
    const firma = this.alan('[data-nm-tax-company]');
    if (secili && firma && secili.dataset.ornek) firma.placeholder = secili.dataset.ornek;
    this.ekiGoster();
  }

  ekiGoster() {
    const ek = this.alan('[data-nm-suffix]');
    if (!ek) return;
    ek.textContent = unvanEki(this.tur(), this.deger('[data-nm-tax-company]'));
    ek.hidden = !ek.textContent;
  }

  hatalar() {
    const tur = this.tur();
    if (!tur) return { tur: 'Firma türünü seçin.' };
    const h = {};
    if (!this.deger('[data-nm-tax-company]')) h.firma = 'Firma unvanını yazın.';
    if (!this.deger('[data-nm-tax-office]')) h.vd = 'Vergi dairesini yazın.';
    if (tur === 'Şahıs') {
      const t = this.deger('[data-nm-tax-tckn]');
      if (!t) h.tc = 'TC kimlik numarasını yazın.';
      else if (!tcknGecerli(t)) h.tc = 'Bu TC no geçersiz görünüyor, lütfen kontrol edin.';
    } else {
      const v = this.deger('[data-nm-tax-no]');
      if (!v) h.vkn = 'Vergi numarasını yazın.';
      else if (v.length !== 10) h.vkn = 'Vergi no 10 hane olmalı.';
    }
    return h;
  }

  tamamMi() {
    return Object.keys(this.hatalar()).length === 0;
  }

  uyarilar() {
    const h = this.hatalar();
    ['tur', 'firma', 'vd', 'vkn', 'tc'].forEach((k) => {
      let mesaj = h[k] || '';
      // Denemeden önce yalnız tam yazılmış ama geçersiz TC uyarılır.
      if (!this.denendi && !(k === 'tc' && this.deger('[data-nm-tax-tckn]').length === 11)) mesaj = '';
      const ipucu = this.alan(`[data-nm-hint="${k}"]`);
      const alan = this.alan(`[data-nm-alan="${k}"]`);
      if (ipucu) {
        ipucu.textContent = mesaj || ipucu.dataset.ipucu || '';
        ipucu.hidden = !ipucu.textContent;
        ipucu.classList.toggle('nm-fatura__ipucu--hata', !!mesaj);
      }
      if (alan) alan.setAttribute('aria-invalid', mesaj ? 'true' : 'false');
    });
  }

  oznitelikler() {
    const tur = this.tur();
    const sahis = tur === 'Şahıs';
    return {
      'Fatura Tipi': 'Kurumsal',
      'Firma Türü': tur,
      Firma: this.deger('[data-nm-tax-company]'),
      'Vergi Dairesi': this.deger('[data-nm-tax-office]'),
      'TC No': sahis ? this.deger('[data-nm-tax-tckn]') : '',
      'Vergi No': sahis ? '' : this.deger('[data-nm-tax-no]'),
    };
  }

  ozetiTazele() {
    if (!this.ozet || !this.ozetMetin) return;
    const acik = !!(this.toggle && this.toggle.checked);
    const tur = this.tur();
    const firma = this.deger('[data-nm-tax-company]');
    const unvan = [firma, firma ? unvanEki(tur, firma) : ''].filter(Boolean).join(' ');
    const vno = this.deger('[data-nm-tax-no]');
    const tno = this.deger('[data-nm-tax-tckn]');
    // Türsüz eski sepette (30 Ekim öncesi) hangisi doluysa o.
    const no = tur === 'Şahıs' ? tno : tur ? vno : vno || tno;
    const parca = [unvan, no].filter(Boolean);
    const kart = this.closest('.nm-fatura');
    if (acik && parca.length) {
      this.ozetMetin.textContent = parca.join(' · ');
      this.ozet.hidden = false;
      if (kart) kart.classList.add('nm-fatura--dolu');
    } else {
      this.ozet.hidden = true;
      if (kart) kart.classList.remove('nm-fatura--dolu');
    }
  }

  bosla() {
    return { 'Fatura Tipi': '', 'Firma Türü': '', 'TC No': '', Firma: '', 'Vergi Dairesi': '', 'Vergi No': '' };
  }

  acKapa() {
    const acik = !!(this.toggle && this.toggle.checked);
    if (acik) {
      this.gonder({ 'Fatura Tipi': 'Kurumsal' });
      const m = this.modal();
      if (m) {
        if (typeof m.show === 'function') m.show();
        else m.setAttribute('open', '');
      }
    } else {
      const m = this.modal();
      if (m) {
        m.querySelectorAll('input[type="text"]').forEach((a) => (a.value = ''));
        m.querySelectorAll('[data-nm-tur]').forEach((r) => (r.checked = false));
      }
      this.denendi = false;
      this.kaydedildi = false;
      this.turuUygula();
      this.uyarilar();
      this.ozetiTazele();
      this.gonder(this.bosla());
    }
  }

  kapandi() {
    if (this.toggle && this.toggle.checked && !this.kaydedildi) {
      if (this.tamamMi()) {
        this.gonder(this.oznitelikler());
        this.kaydedildi = true;
      } else {
        // Yazılanlar kutularda kalıyor; müşteri kutuyu yeniden işaretlerse kaldığı yerden devam eder.
        this.toggle.checked = false;
        this.gonder(this.bosla());
      }
    }
    this.ozetiTazele();
  }

  kaydet() {
    this.denendi = true;
    this.uyarilar();
    if (!this.tamamMi()) {
      const hatali = this.alan('[aria-invalid="true"]');
      const odak = hatali && hatali.matches('fieldset') ? hatali.querySelector('input') : hatali;
      if (odak) odak.focus();
      return;
    }
    this.gonder(this.oznitelikler());
    this.kaydedildi = true;
    this.ozetiTazele();
    const m = this.modal();
    if (m) {
      if (typeof m.hide === 'function') m.hide();
      else m.removeAttribute('open');
    }
  }

  gonder(attributes) {
    const url =
      window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_update_url
        ? FoxTheme.routes.cart_update_url
        : '/cart/update.js';
    const ayar =
      window.FoxTheme && FoxTheme.utils && FoxTheme.utils.fetchConfig
        ? FoxTheme.utils.fetchConfig()
        : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
    return fetch(url, { ...ayar, body: JSON.stringify({ attributes }) }).catch((e) => {
      console.error('Fatura alanlari kaydedilemedi:', e);
    });
  }
}

if (!customElements.get('nm-tax-invoice')) customElements.define('nm-tax-invoice', NmTaxInvoice);
